import { useMemo, useState } from 'react';
import { type ActionMeta, type MultiValue } from 'react-select';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { memoGeneric } from '@/js/libs/memo/index.ts';

import {
  type Translations as SelectionListTranslations,
  SelectionList,
  type Option,
  SortOrder,
  ITEM_MIN_HEIGHT_WITH_BORDER,
} from '../../Components/SelectionList/index.ts';
import { Icons } from '../../index.ts';
import { MultiSelect } from '../Select/index.tsx';
import { SelectListWrapper } from './styles.ts';
import { type FormatOptionLabelProps, type Translations } from './types.ts';

const ITEM_HEIGHT = ITEM_MIN_HEIGHT_WITH_BORDER;

type SelectListProps<T> = {
  dataTestId?: string;
  options?: Option<T>[];
  value?: MultiValue<Option<T>>;
  onChange?: (value: MultiValue<Option<T>>, actionMeta: ActionMeta<Option<T>>) => void;
  initialSelectedSortOrder?: SortOrder;
  translations: Translations;
  visibleRowsCount?: number;
  placeholder?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  getOptionKey?: (option: Option<T>) => string;
  getOptionValue?: (option: Option<T>) => string;
  error?: boolean;
  hideEmptySelectedList?: boolean;
} & FormatOptionLabelProps<T>;

export const SelectList = memoGeneric(function SelectList<T>({
  dataTestId,
  options,
  value = [],
  onChange,
  initialSelectedSortOrder = SortOrder.ASC,
  translations,
  visibleRowsCount = 4,
  placeholder,
  isLoading,
  isDisabled,
  getOptionKey,
  getOptionValue,
  formatOptionLabel,
  getOptionText,
  error,
  hideEmptySelectedList = false,
}: SelectListProps<T>) {
  const [selectedSortOrder, setSelectedSortOrder] = useState<SortOrder>(initialSelectedSortOrder);

  const getSortLabel = useCallbackRef((option: Option<T>) => {
    if (getOptionText) {
      return getOptionText(option);
    }
    return formatOptionLabel?.(option) ?? option.label;
  });

  const selectedOptions = useMemo(
    () =>
      value
        .map((option) => ({ ...option, sortLabel: getSortLabel(option) }))
        .sort((a, b) =>
          selectedSortOrder === SortOrder.ASC ? a.sortLabel.localeCompare(b.sortLabel) : b.sortLabel.localeCompare(a.sortLabel),
        ),
    [value, selectedSortOrder, getSortLabel],
  );

  const handleClear = useCallbackRef(() => {
    onChange?.([], { action: 'clear', removedValues: value });
  });

  const handleRemoveOption = useCallbackRef((option: Option<T>) => {
    const filteredValue = getOptionValue
      ? value.filter((v) => getOptionValue(v) !== getOptionValue(option))
      : value.filter((v) => v.value !== option.value);
    onChange?.(filteredValue, {
      action: 'deselect-option',
      removedValues: [option],
      option,
    });
  });

  const toggleSortOrder = useCallbackRef(() => {
    setSelectedSortOrder((prev) => (prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC));
  });

  const listTranslations: Required<SelectionListTranslations> = useMemo(
    () => ({
      header: translations.selected,
      footer: `${selectedOptions.length} ${translations.totalSeparator} ${options?.length ?? 0}`,
      bulkActionTooltip: translations.clearAll,
      noEntries: translations.noEntries,
    }),
    [options?.length, selectedOptions.length, translations],
  );

  return (
    <SelectListWrapper data-testid={dataTestId}>
      <MultiSelect<Option<T>>
        controlShouldRenderValue={false}
        isClearable={false}
        isDisabled={isDisabled || isLoading}
        options={options}
        value={value}
        onChange={onChange}
        getOptionValue={getOptionValue}
        formatOptionLabel={formatOptionLabel}
        placeholder={placeholder}
      />
      {(!hideEmptySelectedList || selectedOptions.length > 0) && (
        <SelectionList<T>
          dataTestId={`${dataTestId}-selection-list`}
          isLoading={isLoading}
          disabled={isDisabled || !selectedOptions.length}
          options={selectedOptions}
          onRowAction={handleRemoveOption}
          onBulkAction={handleClear}
          sortOrder={selectedSortOrder}
          onChangeSortOrder={toggleSortOrder}
          containerHeight={Math.max(visibleRowsCount, 1) * ITEM_HEIGHT + ITEM_HEIGHT / 2}
          getOptionKey={getOptionKey}
          formatOptionLabel={formatOptionLabel}
          translations={listTranslations}
          bulkActionIcon={<Icons.Clear />}
          rowActionIcon={<Icons.Remove />}
          error={error}
        />
      )}
    </SelectListWrapper>
  );
});
